/* eslint-disable @typescript-eslint/naming-convention */
import { CallState, LoadingState } from '@lcs/shared/api';
import { Comparer, EntityAdapter, EntitySelectors, EntityState, IdSelector, createEntityAdapter } from '@ngrx/entity';
import {
   Action,
   ActionCreator,
   ActionReducer,
   ReducerTypes,
   Selector,
   createFeatureSelector,
   createReducer,
   createSelector,
   on,
} from '@ngrx/store';
import { onReset } from 'src/app/authentication/reset-reducer';
import { CollectionCallState } from './collection-call-state';
import { LoadFailureProps, LoadFromApiProps, LoadFromApiSuccessProps, SearchProps } from './eager-select-actions';
import { EagerSelectSelectors } from './eager-select-selectors';

export function sortOrderSortComparer<T extends { sortOrder: number | null }>(): Comparer<T> {
   return (item1, item2) => {
      return !item1.sortOrder ? -1 : !item2.sortOrder ? 1 : item1.sortOrder >= item2.sortOrder ? 1 : -1;
   };
}

export interface EagerSelectAdapterOptions<T> {
   featureKey: string;
   selectId?: IdSelector<T>;
   sortComparer?: false | Comparer<T>;
}

export interface EagerSelectState<T> extends EntityState<T> {
   callState: CallState;
   fields: string[];
   searchTerm: string | null | undefined;
   url: string | null;
   filters?: string[] | undefined;
}

export type EagerSelectOns<T> = [
   ReducerTypes<EagerSelectState<T>, ActionCreator[]>[],
   ReducerTypes<EagerSelectState<T>, ActionCreator[]>[],
   ReducerTypes<EagerSelectState<T>, ActionCreator[]>[],
   ReducerTypes<EagerSelectState<T>, ActionCreator[]>[],
   ReducerTypes<EagerSelectState<T>, ActionCreator[]>[]
];

export interface EagerSelectAdapter<T> extends Omit<EntityAdapter<T>, 'getSelectors'> {
   getInitialState(): EagerSelectState<T>;
   getInitialState<S extends object>(state: S): EagerSelectState<T> & S;
   getSelectors<S extends object>(
      filter: (items: T[], searchTerm: string | null | undefined) => T[]
   ): EagerSelectSelectors<T, S>;

   /**
    * Gets selectors that select all the parts of an eager select state, where certain selectors
    * will prefer to use the given entities feature state, only falling back to the normal eager
    * select state when the entities feature state does not exist or is not yet populated.
    * @param filter predicate for filtering the list of select options based on the user's search term
    * @param entitiesFeatureToCoalesce A feature selector for en EntityState to use in place of the normal list of options.
    * Only used when it exists and is populated with entities.
    */
   getCoalescingSelectors<S extends object, S2 extends EntityState<T2>, T2 extends T>(
      filter: (items: T[], searchTerm: string | null | undefined) => T[],
      entitiesFeatureToCoalesce: Selector<object, S2>
   ): EagerSelectSelectors<T, S>;
}

export function createEagerSelectAdapter<TState extends EagerSelectState<any>, T extends ExtractSelectItemType<TState>>(
   options: EagerSelectAdapterOptions<T>
): EagerSelectAdapter<T> {
   const entityAdapter = createEntityAdapter(options);

   function getInitialState(state?: object) {
      return {
         ...entityAdapter.getInitialState(state as any),
         callState: LoadingState.Init,
         fields: [] as string[],
         searchTerm: undefined,
         url: '',
      };
   }

   function getCoalescingSelectors<S extends object, S2 extends EntityState<T2>, T2 extends T>(
      filter: (items: T[], searchTerm: string | null | undefined) => T[],
      entitiesFeatureToCoalesce: Selector<object, S2>
   ): EagerSelectSelectors<T, S> {
      const selectState: (state: S) => EagerSelectState<T> = createFeatureSelector<TState>(options.featureKey);
      const entitySelectors: EntitySelectors<T, S> = entityAdapter.getSelectors(selectState);

      const selectSearchTerm = createSelector(selectState, (state) => {
         return state.searchTerm;
      });
      const selectCallState: (state: S) => CallState = createSelector(
         selectState,
         entitiesFeatureToCoalesce,
         (state, entities) => {
            // if there's at least one entity, assume that they're all loaded
            return entities && entities.ids.length > 0 ? LoadingState.Loaded : state.callState;
         }
      );
      const selectFilteredItems: (state: S) => T[] = createSelector(
         entitySelectors.selectAll,
         entitiesFeatureToCoalesce,
         selectSearchTerm,
         (eagerSelectOptions, entityState, searchTerm) => {
            if (entityState && entityState.ids.length > 0) {
               const entities: T2[] = (entityState.ids as any[]).map((id) => {
                  return (entityState.entities as any)[id];
               });
               return filter(entities, searchTerm);
            }
            return filter(eagerSelectOptions, searchTerm);
         }
      );
      const selectAdditionalFields: (state: S) => string[] = createSelector(selectState, (state) => {
         return state.fields;
      });
      const selectCollectionCallState: (state: S) => CollectionCallState = createSelector(
         selectCallState,
         entitySelectors.selectTotal,
         (callState, totalCount): CollectionCallState => {
            return {
               callState,
               totalCount,
            };
         }
      );

      return {
         ...entitySelectors,
         selectAdditionalFields,
         selectCallState,
         selectCollectionCallState,
         selectFilteredItems,
         selectSearchTerm,
         selectState,
      };
   }

   function getSelectors<S extends object>(
      filter: (items: T[], searchTerm: string | null | undefined) => T[]
   ): EagerSelectSelectors<T, S> {
      const selectState: (state: any) => EagerSelectState<T> = createFeatureSelector<TState>(options.featureKey);
      const entitySelectors: EntitySelectors<T, S> = entityAdapter.getSelectors(selectState);

      const selectSearchTerm: (state: S) => string | null | undefined = createSelector(selectState, (state) => {
         return state.searchTerm;
      });
      const selectCallState: (state: S) => CallState = createSelector(selectState, (state) => {
         return state.callState;
      });
      const selectFilteredItems: (state: S) => T[] = createSelector(
         entitySelectors.selectAll,
         selectSearchTerm,
         filter
      );
      const selectAdditionalFields: (state: S) => string[] = createSelector(selectState, (state) => {
         return state.fields;
      });
      const selectCollectionCallState: (state: S) => CollectionCallState = createSelector(
         selectCallState,
         entitySelectors.selectTotal,
         (callState, totalCount): CollectionCallState => {
            return {
               callState,
               totalCount,
            };
         }
      );

      return {
         ...entitySelectors,
         selectAdditionalFields,
         selectCallState,
         selectCollectionCallState,
         selectFilteredItems,
         selectSearchTerm,
         selectState,
      };
   }

   return {
      ...entityAdapter,
      getCoalescingSelectors,
      getInitialState,
      getSelectors,
   };
}

type ExtractSelectItemType<T extends EagerSelectState<any>> = T extends EagerSelectState<infer U> ? U : never;

export function createEagerSelectReducer<
   TState extends EagerSelectState<any>,
   T extends ExtractSelectItemType<TState>,
   TSearch extends string,
   PSearch extends SearchProps,
   TLoadFromApi extends string,
   PLoadFromApi extends LoadFromApiProps,
   TLoadSuccess extends string,
   PLoadSuccess extends LoadFromApiSuccessProps<T>,
   TLoadFailure extends string,
   PLoadFailure extends LoadFailureProps,
   TAction extends Action = Action
>(
   adapter: EagerSelectAdapter<T>,
   initialState: TState,
   search: ActionCreator<TSearch, (props: PSearch) => PSearch & Action<TSearch>>,
   searchReset: ActionCreator,
   loadFromApi: ActionCreator<TLoadFromApi, (props: PLoadFromApi) => PLoadFromApi & Action<TLoadFromApi>>,
   loadSuccess: ActionCreator<TLoadSuccess, (props: PLoadSuccess) => PLoadSuccess & Action<TLoadSuccess>>,
   loadFailure: ActionCreator<TLoadFailure, (props: PLoadFailure) => PLoadFailure & Action<TLoadFailure>>,
   ...ons: ReducerTypes<TState, ActionCreator[]>[]
): ActionReducer<TState, TAction>;
export function createEagerSelectReducer<
   TState extends EagerSelectState<any>,
   T extends ExtractSelectItemType<TState>,
   TSearch extends string,
   PSearch extends SearchProps,
   TLoadFromApi extends string,
   PLoadFromApi extends LoadFromApiProps,
   TLoadSuccess extends string,
   PLoadSuccess extends LoadFromApiSuccessProps<TApiResultItem>,
   TLoadFailure extends string,
   PLoadFailure extends LoadFailureProps,
   TApiResultItem = T,
   TAction extends Action = Action
>(
   adapter: EagerSelectAdapter<T>,
   initialState: TState,
   search: ActionCreator<TSearch, (props: PSearch) => PSearch & Action<TSearch>>,
   searchReset: ActionCreator,
   loadFromApi: ActionCreator<TLoadFromApi, (props: PLoadFromApi) => PLoadFromApi & Action<TLoadFromApi>>,
   loadSuccess: ActionCreator<TLoadSuccess, (props: PLoadSuccess) => PLoadSuccess & Action<TLoadSuccess>>,
   loadFailure: ActionCreator<TLoadFailure, (props: PLoadFailure) => PLoadFailure & Action<TLoadFailure>>,
   map: ((item: TApiResultItem) => T) | null,
   ...ons: ReducerTypes<TState, ActionCreator[]>[]
): ActionReducer<TState, TAction>;
export function createEagerSelectReducer<
   TState extends EagerSelectState<any>,
   T extends ExtractSelectItemType<TState>,
   TSearch extends string,
   PSearch extends SearchProps,
   TLoadFromApi extends string,
   PLoadFromApi extends LoadFromApiProps,
   TLoadSuccess extends string,
   PLoadSuccess extends LoadFromApiSuccessProps<TApiResultItem>,
   TLoadFailure extends string,
   PLoadFailure extends LoadFailureProps,
   TApiResultItem = T,
   TAction extends Action = Action
>(
   adapter: EagerSelectAdapter<T>,
   initialState: TState,
   search: ActionCreator<TSearch, (props: PSearch) => PSearch & Action<TSearch>>,
   searchReset: ActionCreator,
   loadFromApi: ActionCreator<TLoadFromApi, (props: PLoadFromApi) => PLoadFromApi & Action<TLoadFromApi>>,
   loadSuccess: ActionCreator<TLoadSuccess, (props: PLoadSuccess) => PLoadSuccess & Action<TLoadSuccess>>,
   loadFailure: ActionCreator<TLoadFailure, (props: PLoadFailure) => PLoadFailure & Action<TLoadFailure>>,
   mapOrOn: (((item: TApiResultItem) => T) | null) | ReducerTypes<TState, ActionCreator[]> = null,
   ...ons: ReducerTypes<TState, ActionCreator[]>[]
): ActionReducer<TState, TAction> {
   let mapFn: ((item: TApiResultItem) => T) | null = null;
   if (mapOrOn && isMapFn(mapOrOn)) {
      mapFn = mapOrOn;
   }

   return createReducer(
      initialState,
      onReset(initialState),
      on(search, (state, action) => {
         return {
            ...state,
            searchTerm: action.searchTerm,
         };
      }),
      on(searchReset, (state, _action) => {
         if (state.searchTerm == null || state.searchTerm.trim() === '') {
            return state;
         }
         return {
            ...state,
            searchTerm: null,
         };
      }),
      on(loadFromApi, (state, _action) => {
         return {
            ...adapter.removeAll(state),
            callState: LoadingState.Loading,
            totalCount: 0,
         };
      }),
      on(loadSuccess, (state, action) => {
         const items = action.items || [];
         // we assume that if there's no mapping function, then the api's response
         // is the same type that we want in the store
         const mapped = mapFn ? items.map(mapFn) : (items as any as T[]);
         return {
            ...adapter.setAll(mapped, state),
            callState: LoadingState.Loaded,
            fields: action.fields,
            filters: action.filters,
            totalCount: action.totalCount,
            url: action.url,
         };
      }),
      on(loadFailure, (state, action) => {
         return {
            ...adapter.removeAll(state),
            callState: action.error,
            totalCount: 0,
         };
      }),
      ...ons
   );
}

function isMapFn<S, TApiResultItem, T>(
   mapFn: ((item: TApiResultItem) => T) | ReducerTypes<S, ActionCreator[]>
): mapFn is (item: TApiResultItem) => T {
   return typeof mapFn === 'function';
}
